/**
 * Mangelsregister - Oversikt over mangler for en sak
 * For boligtvister med strukturert håndtering av mangler, bevis og reklamasjoner
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import MangelDialog from './MangelDialog';
import ReklamasjonDialog from './ReklamasjonDialog';
import './Mangelsregister.css';

export default function Mangelsregister({ sakId }) {
  const [mangler, setMangler] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [visMangelDialog, setVisMangelDialog] = useState(false);
  const [redigerMangel, setRedigerMangel] = useState(null);
  const [visReklamasjonDialog, setVisReklamasjonDialog] = useState(false);
  const [valgtMangel, setValgtMangel] = useState(null);
  const [utvidetMangel, setUtvidetMangel] = useState(null);

  // Filtre
  const [filter, setFilter] = useState({
    status: '',
    alvorlighetsgrad: '',
    søk: ''
  });

  // Sortering
  const [sortering, setSortering] = useState({
    felt: 'alvorlighetsgrad',
    retning: 'desc'
  });

  // Statistikk
  const [statistikk, setStatistikk] = useState({
    totalt: 0,
    kritisk: 0,
    alvorlig: 0,
    identifisert: 0,
    reklamert: 0,
    totalKostnad: 0
  });

  useEffect(() => {
    if (sakId) {
      lastMangler();
    }
  }, [sakId, filter, sortering]);

  const lastMangler = async () => {
    try {
      setLoading(true);

      // Bygg query params
      const params = new URLSearchParams({ sak_id: sakId });
      if (filter.status) params.append('status', filter.status);
      if (filter.alvorlighetsgrad) params.append('alvorlighetsgrad', filter.alvorlighetsgrad);

      const data = await crmApi.request(`/mangler?${params.toString()}`);

      let mangelList = data.mangler || [];

      // Søkefiltrering (lokalt)
      if (filter.søk) {
        const søkeord = filter.søk.toLowerCase();
        mangelList = mangelList.filter(m =>
          m.bygningsdel?.toLowerCase().includes(søkeord) ||
          m.mangelbeskrivelse?.toLowerCase().includes(søkeord) ||
          m.fysisk_plassering?.toLowerCase().includes(søkeord) ||
          m.rom?.toLowerCase().includes(søkeord)
        );
      }

      // Sortering
      mangelList.sort((a, b) => {
        let aVal = a[sortering.felt];
        let bVal = b[sortering.felt];

        // Håndter spesialtilfeller
        if (sortering.felt === 'alvorlighetsgrad') {
          const verdier = { kritisk: 4, alvorlig: 3, middels: 2, mindre: 1 };
          aVal = verdier[a.alvorlighetsgrad] || 0;
          bVal = verdier[b.alvorlighetsgrad] || 0;
        }

        if (aVal < bVal) return sortering.retning === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortering.retning === 'asc' ? 1 : -1;
        return 0;
      });

      setMangler(mangelList);

      // Beregn statistikk
      beregnStatistikk(mangelList);
    } catch (error) {
      console.error('Feil ved lasting av mangler:', error);
    } finally {
      setLoading(false);
    }
  };

  const beregnStatistikk = (mangelList) => {
    const stats = {
      totalt: mangelList.length,
      kritisk: mangelList.filter(m => m.alvorlighetsgrad === 'kritisk').length,
      alvorlig: mangelList.filter(m => m.alvorlighetsgrad === 'alvorlig').length,
      identifisert: mangelList.filter(m => m.status === 'identifisert').length,
      reklamert: mangelList.filter(m => m.status === 'reklamert').length,
      totalKostnad: mangelList.reduce((sum, m) => sum + (m.estimert_kostnad || 0), 0)
    };
    setStatistikk(stats);
  };

  const slettMangel = async (mangelId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne mangelen?\n\nDette vil også slette tilknyttede reklamasjoner.')) {
      return;
    }

    try {
      await crmApi.request(`/mangler/${mangelId}`, {
        method: 'DELETE'
      });

      alert('✓ Mangel slettet');
      lastMangler();
    } catch (error) {
      console.error('Feil ved sletting av mangel:', error);
      alert('Kunne ikke slette mangel');
    }
  };

  const toggleUtvidMangel = (mangelId) => {
    setUtvidetMangel(utvidetMangel === mangelId ? null : mangelId);
  };

  const getAlvorlighetsgradsIkon = (grad) => {
    switch (grad) {
      case 'kritisk': return '🔴';
      case 'alvorlig': return '🟠';
      case 'middels': return '🟡';
      case 'mindre': return '🟢';
      default: return '⚪';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      identifisert: { tekst: 'Identifisert', klasse: 'status-identifisert' },
      dokumentert: { tekst: 'Dokumentert', klasse: 'status-dokumentert' },
      reklamert: { tekst: 'Reklamert', klasse: 'status-reklamert' },
      akseptert: { tekst: 'Akseptert', klasse: 'status-akseptert' },
      bestridt: { tekst: 'Bestridt', klasse: 'status-bestridt' },
      utbedret: { tekst: 'Utbedret', klasse: 'status-utbedret' },
      kompensert: { tekst: 'Kompensert', klasse: 'status-kompensert' },
      avvist: { tekst: 'Avvist', klasse: 'status-avvist' }
    };

    const badge = badges[status] || { tekst: status, klasse: '' };
    return <span className={`status-badge ${badge.klasse}`}>{badge.tekst}</span>;
  };

  const formaterBeløp = (beløp) => {
    if (!beløp) return '-';
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(beløp);
  };

  return (
    <div className="mangelsregister">
      {/* Header med statistikk */}
      <div className="mangelsregister-header">
        <div className="header-tittel">
          <h2>📋 Mangelsregister</h2>
          <button
            className="btn btn-primary"
            onClick={() => {
              setRedigerMangel(null);
              setVisMangelDialog(true);
            }}
          >
            ➕ Ny mangel
          </button>
        </div>

        <div className="statistikk-kort">
          <div className="stat-item">
            <div className="stat-tall">{statistikk.totalt}</div>
            <div className="stat-label">Totalt mangler</div>
          </div>
          <div className="stat-item stat-kritisk">
            <div className="stat-tall">{statistikk.kritisk}</div>
            <div className="stat-label">Kritiske</div>
          </div>
          <div className="stat-item stat-alvorlig">
            <div className="stat-tall">{statistikk.alvorlig}</div>
            <div className="stat-label">Alvorlige</div>
          </div>
          <div className="stat-item">
            <div className="stat-tall">{statistikk.reklamert}</div>
            <div className="stat-label">Reklamert</div>
          </div>
          <div className="stat-item stat-kostnad">
            <div className="stat-tall">{formaterBeløp(statistikk.totalKostnad)}</div>
            <div className="stat-label">Estimert kostnad</div>
          </div>
        </div>
      </div>

      {/* Filtre */}
      <div className="mangelsregister-filtre">
        <input
          type="text"
          className="søkefelt"
          placeholder="🔍 Søk i mangler..."
          value={filter.søk}
          onChange={(e) => setFilter({ ...filter, søk: e.target.value })}
        />

        <select
          value={filter.alvorlighetsgrad}
          onChange={(e) => setFilter({ ...filter, alvorlighetsgrad: e.target.value })}
        >
          <option value="">Alle alvorlighetsgrader</option>
          <option value="kritisk">🔴 Kritisk</option>
          <option value="alvorlig">🟠 Alvorlig</option>
          <option value="middels">🟡 Middels</option>
          <option value="mindre">🟢 Mindre</option>
        </select>

        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Alle statuser</option>
          <option value="identifisert">Identifisert</option>
          <option value="dokumentert">Dokumentert</option>
          <option value="reklamert">Reklamert</option>
          <option value="akseptert">Akseptert</option>
          <option value="bestridt">Bestridt</option>
          <option value="utbedret">Utbedret</option>
        </select>

        <select
          value={sortering.felt}
          onChange={(e) => setSortering({ ...sortering, felt: e.target.value })}
        >
          <option value="alvorlighetsgrad">Sorter: Alvorlighetsgrad</option>
          <option value="created_at">Sorter: Dato opprettet</option>
          <option value="bygningsdel">Sorter: Bygningsdel</option>
          <option value="estimert_kostnad">Sorter: Kostnad</option>
        </select>
      </div>

      {/* Mangelliste */}
      {loading ? (
        <div className="loading">Laster mangler...</div>
      ) : mangler.length === 0 ? (
        <div className="tom-liste">
          <p>📋 Ingen mangler registrert ennå</p>
          <p className="tom-liste-hint">Klikk "Ny mangel" for å komme i gang</p>
        </div>
      ) : (
        <div className="mangel-liste">
          {mangler.map((mangel) => (
            <div
              key={mangel.id}
              className={`mangel-kort ${utvidetMangel === mangel.id ? 'utvidet' : ''}`}
            >
              {/* Mangel header */}
              <div
                className="mangel-header"
                onClick={() => toggleUtvidMangel(mangel.id)}
              >
                <div className="mangel-header-venstre">
                  <span className="alvorlighetsgrad-ikon">
                    {getAlvorlighetsgradsIkon(mangel.alvorlighetsgrad)}
                  </span>
                  <div className="mangel-info">
                    <h3>{mangel.bygningsdel}</h3>
                    <p className="mangel-plassering">
                      {mangel.rom && `${mangel.rom} - `}
                      {mangel.fysisk_plassering || 'Ingen plassering angitt'}
                    </p>
                  </div>
                </div>

                <div className="mangel-header-høyre">
                  {getStatusBadge(mangel.status)}
                  <span className="mangel-kostnad">{formaterBeløp(mangel.estimert_kostnad)}</span>
                  <span className="expand-ikon">
                    {utvidetMangel === mangel.id ? '▼' : '▶'}
                  </span>
                </div>
              </div>

              {/* Mangel detaljer (vises når utvidet) */}
              {utvidetMangel === mangel.id && (
                <div className="mangel-detaljer">
                  <div className="mangel-beskrivelse">
                    <h4>Beskrivelse:</h4>
                    <p>{mangel.mangelbeskrivelse}</p>
                  </div>

                  {mangel.detaljer && (
                    <div className="mangel-ytterligere">
                      <h4>Ytterligere detaljer:</h4>
                      {mangel.detaljer.symptomer && (
                        <p><strong>Symptomer:</strong> {mangel.detaljer.symptomer}</p>
                      )}
                      {mangel.detaljer.årsak && (
                        <p><strong>Årsak:</strong> {mangel.detaljer.årsak}</p>
                      )}
                      {mangel.detaljer.konsekvenser && (
                        <p><strong>Konsekvenser:</strong> {mangel.detaljer.konsekvenser}</p>
                      )}
                    </div>
                  )}

                  {/* Bevis for denne mangelen */}
                  {mangel.bevis && mangel.bevis.length > 0 && (
                    <div className="mangel-bevis">
                      <h4>📎 Bevis ({mangel.bevis.length})</h4>
                      <div className="bevis-liste">
                        {mangel.bevis.map((bevis) => (
                          <div key={bevis.id} className="bevis-item">
                            <span className="bevis-type-ikon">
                              {bevis.bevistype === 'forskrift_brudd' && '⚖️'}
                              {bevis.bevistype === 'berettiget_forventning' && '🎯'}
                              {bevis.bevistype === 'tilbakeholdt_opplysning' && '🔒'}
                              {bevis.bevistype === 'uriktig_opplysning' && '❌'}
                              {bevis.bevistype === 'teknisk_rapport' && '📊'}
                              {bevis.bevistype === 'fotodokumentasjon' && '📷'}
                              {bevis.bevistype === 'korrespondanse' && '✉️'}
                              {bevis.bevistype === 'vitneforklaring' && '👤'}
                            </span>
                            <span className="bevis-beskrivelse">{bevis.beskrivelse || bevis.bevistype}</span>
                            <span className={`bevis-vekt bevis-vekt-${bevis.bevisvekt}`}>
                              {bevis.bevisvekt}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reklamasjoner for denne mangelen */}
                  {mangel.reklamasjoner && mangel.reklamasjoner.length > 0 && (
                    <div className="mangel-reklamasjoner">
                      <h4>📨 Reklamasjoner ({mangel.reklamasjoner.length})</h4>
                      <div className="reklamasjon-liste">
                        {mangel.reklamasjoner.map((rekl) => (
                          <div key={rekl.id} className="reklamasjon-item">
                            <div className="rekl-dato">{new Date(rekl.reklamasjonsdato).toLocaleDateString('nb-NO')}</div>
                            <div className="rekl-mottaker">→ {rekl.mottaker}</div>
                            <div className="rekl-metode">({rekl.metode})</div>
                            <div className={`rekl-svar ${rekl.svar_mottatt ? 'mottatt' : 'venter'}`}>
                              {rekl.svar_mottatt ? '✓ Svar mottatt' : '⏳ Venter på svar'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Handlinger */}
                  <div className="mangel-handlinger">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setValgtMangel(mangel);
                        setVisReklamasjonDialog(true);
                      }}
                    >
                      📨 Ny reklamasjon
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setRedigerMangel(mangel);
                        setVisMangelDialog(true);
                      }}
                    >
                      ✏️ Rediger
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => slettMangel(mangel.id)}
                    >
                      🗑️ Slett
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialoger */}
      {visMangelDialog && (
        <MangelDialog
          sakId={sakId}
          mangel={redigerMangel}
          onClose={() => {
            setVisMangelDialog(false);
            setRedigerMangel(null);
            lastMangler();
          }}
        />
      )}

      {visReklamasjonDialog && (
        <ReklamasjonDialog
          sakId={sakId}
          mangelId={valgtMangel?.id}
          onClose={() => {
            setVisReklamasjonDialog(false);
            setValgtMangel(null);
            lastMangler();
          }}
        />
      )}
    </div>
  );
}
